import { useMemo, useState } from "react"
import { LuPlus, LuSearch } from "react-icons/lu"
import { useQuery } from "react-query"
import { Link } from "react-router-dom"

import { listClients } from "~/v2/api/clients"
import { listCourses } from "~/v2/api/vaas"
import Button from "~/v2/components/ui/button"
import VaasCardList from "~/v2/components/vaas/vaas-card-list"
import { mapCourseToVaasListItem } from "~/v2/lib/vaas-mapper"
import { VAAS_COURSES_QUERY_KEY } from "~/v2/lib/vaas-query-keys"
import type {
  ClientSummary,
  VaasCourse,
  VaasId,
  VaasListItem,
} from "~/v2/types/vaas"

const statusOptions = ["all", "published", "unpublished", "draft", "archived"]

type StatusFilter = "all" | VaasListItem["status"]

const normalizeId = (value?: VaasId | null | ""): string | null => {
  if (value === null || value === undefined || value === "") {
    return null
  }

  return String(value)
}

const clientHasCourse = (
  client: ClientSummary | null,
  currentCourse?: VaasCourse | null,
): boolean =>
  (client?.courses || []).some(
    (course) => normalizeId(course?.id) === normalizeId(currentCourse?.id),
  )

const getClientVaasCount = (
  client: ClientSummary,
  vaasCourses: VaasCourse[],
): number =>
  vaasCourses.filter((course) => clientHasCourse(client, course)).length

const getSelectedClient = (
  clients: ClientSummary[],
  clientId: string,
): ClientSummary | null =>
  clients.find((client) => normalizeId(client.id) === clientId) || null

const List = () => {
  const [searchValue, setSearchValue] = useState("")
  const [status, setStatus] = useState<StatusFilter>("all")
  const [clientId, setClientId] = useState("all")

  const {
    data: courses = [],
    isLoading,
    isError,
  } = useQuery(VAAS_COURSES_QUERY_KEY, () => listCourses())
  const {
    data: clients = [],
    isError: isClientsError,
    isLoading: isClientsLoading,
  } = useQuery("clients", () => listClients())

  const vaasCourses = useMemo(
    () => courses.filter((course: VaasCourse) => course.course_type === "vaas"),
    [courses],
  )

  const clientOptions = useMemo(() => {
    return clients
      .map((client) => ({
        id: normalizeId(client.id),
        name: client.name || `Client ${client.id}`,
        vaasCount: getClientVaasCount(client, vaasCourses),
      }))
      .filter((client) => client.id && client.vaasCount > 0)
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [clients, vaasCourses])

  const selectedClient = useMemo(
    () => (clientId === "all" ? null : getSelectedClient(clients, clientId)),
    [clientId, clients],
  )

  const vaasItems = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase()

    return vaasCourses
      .map(mapCourseToVaasListItem)
      .filter((item) => (status === "all" ? true : item.status === status))
      .filter((item) => {
        if (clientId === "all") {
          return true
        }

        return clientHasCourse(selectedClient, item.course)
      })
      .filter((item) => {
        if (!normalizedSearch) {
          return true
        }

        return (
          item.name.toLowerCase().includes(normalizedSearch) ||
          String(item.containerId).includes(normalizedSearch) ||
          String(item.courseId).includes(normalizedSearch) ||
          String(item.vaasId || "").includes(normalizedSearch)
        )
      })
      .sort(
        (a, b) =>
          (b.course.updated?.unix?.() || 0) - (a.course.updated?.unix?.() || 0),
      )
  }, [clientId, searchValue, selectedClient, status, vaasCourses])

  return (
    <div className="h-full w-full overflow-auto bg-background">
      <div className="mx-auto flex h-full w-full max-w-6xl flex-col gap-4 px-6 py-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-[-0.02em] text-foreground">
              VAAS
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage VAAS-only course and lesson pairs from one dedicated list.
            </p>
          </div>
          <Link to="/vaas/create">
            <Button className="h-9 rounded-md px-4 text-sm">
              <LuPlus className="mr-2 h-4 w-4" />
              New VAAS
            </Button>
          </Link>
        </div>

        <div>
          <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_150px_220px]">
            <label className="relative block">
              <LuSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                className="h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm text-foreground outline-none ring-0 transition-colors placeholder:text-muted-foreground focus:border-primary"
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search by name, container ID, or VAAS ID..."
                value={searchValue}
              />
            </label>
            <label className="block">
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus:border-primary"
                onChange={(event) =>
                  setStatus(event.target.value as StatusFilter)
                }
                value={status}
              >
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option === "all"
                      ? `All (${vaasCourses.length})`
                      : option[0].toUpperCase() + option.slice(1)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground outline-none transition-colors focus:border-primary disabled:cursor-not-allowed disabled:opacity-60"
                disabled={isClientsLoading || isClientsError}
                onChange={(event) => setClientId(event.target.value)}
                value={clientId}
              >
                <option value="all">
                  {isClientsError
                    ? "Clients unavailable"
                    : `All clients (${clientOptions.length})`}
                </option>
                {clientOptions.map((option) => (
                  <option key={option.id} value={option.id || ""}>
                    {option.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {!isLoading && !isError && (
          <div className="text-sm text-muted-foreground">
            Showing {vaasItems.length} of {vaasCourses.length} items
          </div>
        )}

        {isLoading && (
          <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
            Loading VAAS items...
          </div>
        )}

        {isError && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-6 text-sm text-destructive">
            Failed to load VAAS items. Refresh and try again.
          </div>
        )}

        {!isLoading && !isError && <VaasCardList items={vaasItems} />}
      </div>
    </div>
  )
}

export default List
